import PropTypes from 'prop-types';
import { Breadcrumb } from 'antd';
import { Link } from 'react-router-dom';
import { getSubjectColorIndex } from '../../../core/theme/palette';
import './primitives.css';

/**
 * RecordHead — the record archetype's head (`design/web/archetypes/record.md` §2).
 *
 * One shape for four unrelated screens: a student's statement, a lesson's
 * register, a group's roster, a question set. Each is an identity, one figure
 * that answers the question the page exists to answer, and a handful of facts —
 * so they get one head instead of four different banners.
 *
 * It renders two things `--space-5` apart: a 40px header band (breadcrumb left,
 * actions right, **no title** — the record's name is not written twice) and the
 * head card (identity · hero figure · fact rail). Below 768 the band merges into
 * the card — the breadcrumb becomes the head's first line, the actions collapse
 * to `overflowMenu`, and the hero drops to its own band.
 *
 *   <RecordHead
 *     breadcrumbs={[{ label, path }, { label }]}
 *     actions={<Button …/>}            // desktop band actions
 *     overflowMenu={<Dropdown …/>}      // phone crumb-line ⋯ (optional)
 *     tile={<IconTile … size="md" />}
 *     name="Matematika A-2"
 *     meta="12-dars · 14:00–16:00 · 3-xona"
 *     hero={{ figure: 12, unit: '/ 15 keldi', tone: 'garnet',
 *             pill: <span …/>, ariaLabel: '12 / 15 keldi', live: true }}
 *     facts={[{ key: 'Mavzu', value: '…' }, …]}
 *     subject={courseName}              // colours the wash (or `tone`)
 *   />
 *
 * The hero figure never animates and takes a hue only when its value is a state
 * (the balance is garnet when owed; a count stays ink). `subject` wins over
 * `tone` for the wash, routing through the same hash the tile uses so head and
 * tile share one hue.
 */

const WASH_TONES = [
  'majolica',
  'pistachio',
  'saffron',
  'garnet',
  'plum',
  'terracotta',
  'indigo',
  'primary',
];

const washClass = (subject, tone) => {
  const hasSubject = subject !== undefined && subject !== null && subject !== '';
  if (hasSubject) return `lms-record-head--wash-subj-${getSubjectColorIndex(subject)}`;
  return WASH_TONES.includes(tone) ? `lms-record-head--wash-${tone}` : '';
};

const Crumbs = ({ breadcrumbs }) => (
  <Breadcrumb
    items={breadcrumbs.map((b) => ({
      title: b.path ? <Link to={b.path}>{b.label}</Link> : b.label,
    }))}
  />
);

Crumbs.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
};

const Hero = ({ hero }) => {
  if (!hero) return null;
  const figClass = [
    'lms-record-head__fig',
    'figure',
    hero.tone ? `lms-record-head__fig--${hero.tone}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const hasFigure = hero.figure !== undefined && hero.figure !== null;
  return (
    <div className="lms-record-head__hero">
      {hasFigure && (
        // The label wraps only the figure, so the state pill beside it stays
        // readable; the digits are hidden and the whole quantity announced once.
        <span
          className="lms-record-head__hero-fig"
          aria-label={hero.ariaLabel || undefined}
          aria-live={hero.live ? 'polite' : undefined}
        >
          <span aria-hidden={hero.ariaLabel ? true : undefined}>
            <span className={figClass}>{hero.figure}</span>
            {hero.unit && <span className="lms-record-head__unit">{hero.unit}</span>}
          </span>
        </span>
      )}
      {hero.pill}
    </div>
  );
};

Hero.propTypes = {
  hero: PropTypes.shape({
    figure: PropTypes.node,
    unit: PropTypes.node,
    tone: PropTypes.string,
    pill: PropTypes.node,
    ariaLabel: PropTypes.string,
    live: PropTypes.bool,
  }),
};

const RecordHead = ({
  breadcrumbs = [],
  actions,
  overflowMenu,
  tile,
  name,
  meta,
  hero,
  facts = [],
  subject,
  tone,
  className = '',
}) => (
  <>
    {(breadcrumbs.length > 0 || actions) && (
      <div className="lms-record-head__band">
        <div className="lms-record-head__band-crumb">
          {breadcrumbs.length > 0 && <Crumbs breadcrumbs={breadcrumbs} />}
        </div>
        {actions && <div className="lms-record-head__band-actions">{actions}</div>}
      </div>
    )}

    <section
      className={['lms-record-head', washClass(subject, tone), className]
        .filter(Boolean)
        .join(' ')}
    >
      {(breadcrumbs.length > 0 || overflowMenu) && (
        <div className="lms-record-head__crumb">
          {breadcrumbs.length > 0 && <Crumbs breadcrumbs={breadcrumbs} />}
          <span className="lms-record-head__crumb-spacer" />
          {overflowMenu}
        </div>
      )}

      <div className="lms-record-head__id">
        {tile}
        <div className="lms-record-head__text">
          <div className="lms-record-head__name">{name}</div>
          {meta != null && meta !== '' && (
            <div className="lms-record-head__meta">{meta}</div>
          )}
        </div>
        <Hero hero={hero} />
      </div>

      {facts.length > 0 && (
        <dl className="lms-record-head__facts">
          {facts.map((fact) => (
            <div className="lms-record-head__fact" key={fact.key}>
              <dt className="lms-record-head__fact-k">{fact.key}</dt>
              <dd className="lms-record-head__fact-v">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  </>
);

RecordHead.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      path: PropTypes.string,
    }),
  ),
  /** Desktop band actions — one primary plus any record-scoped control. */
  actions: PropTypes.node,
  /** Phone crumb-line trailing control, for the actions the band drops. */
  overflowMenu: PropTypes.node,
  /** An IconTile in the record's hue. */
  tile: PropTypes.node,
  name: PropTypes.node.isRequired,
  meta: PropTypes.node,
  hero: Hero.propTypes.hero,
  facts: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.node.isRequired,
      value: PropTypes.node,
    }),
  ),
  /** Course id/code/name — colours the wash deterministically (wins over tone). */
  subject: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** A functional tone for the wash when there is no subject. */
  tone: PropTypes.string,
  className: PropTypes.string,
};

export default RecordHead;
